import { VNode, VueConstructor } from 'vue';
import { DirectiveBinding } from 'vue/types/options';

/*
  Usage:

   as directive
   <div v-debounce="onDebounce"></div>

   as a function
   this.$debounce(()=>{
      //this is debounced
   },500,this)();

   // Make sure to call the returned function at the end --> ();
*/

declare module 'vue/types/vue' {
  interface Vue {
    $debounce: <T extends Function>(cb: T, wait: number, lockingObject?: any) => T
  }
  interface VueConstructor {
    $debounce: <T extends Function>(cb: T, wait: number, lockingObject?: any) => T
  }
}

export default {
  install: (Vue: VueConstructor, options: any) => {
    function debounce<T extends Function>(cb: T, wait = 300, lockingObject: any = {}) {
      const callable = (...args: any) => {
        clearTimeout(lockingObject.$debounceLock);
        lockingObject.$debounceLock = setTimeout(() => {
          cb(...args);
        }, wait);
      };
      return <T>(<any>callable);
    }

    function directive(el: HTMLElement, binding: DirectiveBinding, vnode: VNode) {
      // Only rebind if a new callback was passed
      if (binding.value !== binding.oldValue) {
        const debouncedFunction = debounce((evt: Event) => {
          if (typeof binding.value === 'function') {
            binding.value(evt);
          }
          if (vnode.context !== undefined && typeof vnode.context.$emit === 'function') {
            vnode.context.$emit('debounce', evt);
          }
        });

        // All events that should be debounced
        if (vnode.componentInstance !== undefined) {
          vnode.componentInstance.$on('input', debouncedFunction);
        }
        el.oninput = debouncedFunction;
        el.onchange = debouncedFunction;
      }
    }

    Vue.directive('debounce', directive);
    Vue.$debounce = debounce;
    Vue.prototype.$debounce = Vue.$debounce;
  },
};
